import { Box, Flex, Textarea } from '@chakra-ui/react';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { Text } from '@web-app/components/Typography/Text';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { MealPlanInstructionsEntityResponse } from '@web-app/server/api/types';
import { useMolecule } from 'bunshi/react';
import { useAtom, useAtomValue } from 'jotai';
import { ChangeEventHandler, useCallback, useEffect, useRef } from 'react';
import ResizeTextarea from 'react-textarea-autosize';

import { BerryLoader } from '../BerryLoader';
import { MealPlanStepHeader } from './MealPlanStepHeader';
import { RegenerateButton } from './RegenerateButton';
import { MealPlanView } from './utils';

const EMPTY_INSTRUCTIONS: MealPlanInstructionsEntityResponse = {
  id: '',
  dietaryRestrictionsAndAllergies: '',
  foodPreferences: '',
  calorieAndMacroRequirements: '',
  householdConsiderations: '',
  otherGuidance: '',
};

export interface MealPlanInstructionsProps {
  readOnly: boolean;
}

export const MealPlanInstructions = ({
  readOnly,
}: MealPlanInstructionsProps) => {
  const {
    mealPlanInstructionsAtom,
    areMealPlanInstructionsLoadingAtom,
    isMealPlanBusyAtom,
    analyticsContextAtom,
  } = useMolecule(mealPlanMolecule);
  const [mealPlanInstructions, setMealPlanInstructions] = useAtom(
    mealPlanInstructionsAtom,
  );
  const isPending = useAtomValue(areMealPlanInstructionsLoadingAtom);
  const isBusy = useAtomValue(isMealPlanBusyAtom);
  const analyticsContext = useAtomValue(analyticsContextAtom);
  const { trackEvent } = useAnalytics();
  useEffect(
    function trackInstructionsViewEvent() {
      trackEvent(EVENT_NAMES.VIEW_MEAL_PLAN_INSTRUCTIONS, {
        context: analyticsContext,
      });
    },
    [trackEvent, analyticsContext],
  );

  if (isPending || isBusy) {
    return (
      <Flex
        width="100%"
        minHeight="300px"
        flex={1}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <BerryLoader />
        <Text>
          {isBusy
            ? 'Regenerating meal plan preferences...'
            : 'Loading pre-meal plan data...'}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" gap={7} mb={8}>
      <Flex justifyContent="space-between" alignItems="flex-end">
        <MealPlanStepHeader step={MealPlanView.Instructions} />
        {!readOnly && <RegenerateButton step={MealPlanView.Instructions} />}
      </Flex>
      <MealPlanInstructionsEditBlock
        mealPlanInstructions={mealPlanInstructions ?? EMPTY_INSTRUCTIONS}
        setMealPlanInstructions={setMealPlanInstructions}
        readOnly={readOnly}
      />
    </Flex>
  );
};

export const MealPlanInstructionsEditBlock = ({
  mealPlanInstructions,
  setMealPlanInstructions,
  readOnly,
}: {
  mealPlanInstructions: MealPlanInstructionsEntityResponse;
  setMealPlanInstructions: (
    data: MealPlanInstructionsEntityResponse | null,
  ) => void;
  readOnly: boolean;
}) => {
  const { trackEvent } = useAnalytics();
  const { analyticsContextAtom } = useMolecule(mealPlanMolecule);
  const analyticsContext = useAtomValue(analyticsContextAtom);
  const editAlreadyRun = useRef(false);
  const trackInstructionChange = useCallback(() => {
    if (!editAlreadyRun.current) {
      editAlreadyRun.current = true;
      trackEvent(EVENT_NAMES.EDITS_MEAL_PLAN_INSTRUCTIONS, {
        context: analyticsContext,
      });
    }
  }, [trackEvent, analyticsContext]);
  return (
    <>
      <MealPlanInstructionInput
        value={mealPlanInstructions.dietaryRestrictionsAndAllergies}
        prompt={'Dietary restrictions and allergies'}
        placeholder={"Is there anything the patient can't or shouldn't eat?"}
        readOnly={readOnly}
        onChange={(e) => {
          trackInstructionChange();
          setMealPlanInstructions({
            ...mealPlanInstructions,
            dietaryRestrictionsAndAllergies: e.target.value,
          });
        }}
      />
      <MealPlanInstructionInput
        value={mealPlanInstructions.foodPreferences}
        prompt={'Food preferences'}
        placeholder={
          'If they have likes and dislikes, tell us about them here so they can be included or excluded'
        }
        readOnly={readOnly}
        onChange={(e) => {
          trackInstructionChange();
          setMealPlanInstructions({
            ...mealPlanInstructions,
            foodPreferences: e.target.value,
          });
        }}
      />
      <MealPlanInstructionInput
        value={mealPlanInstructions.calorieAndMacroRequirements}
        prompt={'Calorie and macro requirements'}
        placeholder={
          "If you have a specific number of calories or macro requirements per meal, include that here. Be as specific as you'd like."
        }
        readOnly={readOnly}
        onChange={(e) => {
          trackInstructionChange();
          setMealPlanInstructions({
            ...mealPlanInstructions,
            calorieAndMacroRequirements: e.target.value,
          });
        }}
      />
      <MealPlanInstructionInput
        value={mealPlanInstructions.householdConsiderations}
        prompt={'Household considerations'}
        placeholder={
          'Does your patient cook for someone who is vegan or gluten free? Let us know so we can adjust their plan accordingly.'
        }
        readOnly={readOnly}
        onChange={(e) => {
          trackInstructionChange();
          setMealPlanInstructions({
            ...mealPlanInstructions,
            householdConsiderations: e.target.value,
          });
        }}
      />
      <MealPlanInstructionInput
        value={mealPlanInstructions.otherGuidance}
        prompt={'Other guidance'}
        placeholder={
          "Include any other instructions or considerations you'd like to be considered in the draft of this meal plan"
        }
        readOnly={readOnly}
        onChange={(e) => {
          trackInstructionChange();
          setMealPlanInstructions({
            ...mealPlanInstructions,
            otherGuidance: e.target.value,
          });
        }}
      />
    </>
  );
};

export const MealPlanInstructionInput = ({
  value,
  prompt,
  placeholder,
  readOnly,
  onChange,
}: {
  value: string;
  prompt: string;
  placeholder: string;
  readOnly: boolean;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
}) => {
  return (
    <Box>
      <Text textType="sm" fontWeight={600} marginBottom="8px">
        {prompt}
      </Text>
      {readOnly ? (
        <Text textType="sm">{value || <em>None provided</em>}</Text>
      ) : (
        <Textarea
          as={ResizeTextarea}
          borderColor="border.default"
          backgroundColor="white"
          rows={1}
          minRows={1}
          maxRows={10}
          py={1.5}
          px={3}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
      )}
    </Box>
  );
};
